import {
    IServerQuestionCategory,
    IClientQuestionCategory,
    IServerChat,
    IClientChat,
    IClientChatHistoryMessage,
    IClientChatHistory,
    IServerChatHistory,
    IServerChatHistoryMessage,
    IQuestionAnswer,
    ICompanyQuestion,
    MessageTypes,
    TempMessageAndChatIds,
    ICompanyAnsweredQuestion,
    CompanyQuestionType,
} from '../../../common';
import { IOption } from '../../../base';

const stripHtml = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

export const formatCategoriesFromServer = (
    categories: Array<IServerQuestionCategory>
): Array<IClientQuestionCategory> => {
    return categories.map((category) => {
        return {
            id: category.category_id,
            name: category.category_name,
            percentage: category.percentage,
            questions: category.unanswered,
        };
    });
};

export const formatChatsFromServer = (
    chats: Array<IServerChat>
): Array<IClientChat> => {
    return chats.map((chat) => {
        return {
            id: chat.id,
            title: chat.title,
            uuid: chat.uuid,
        };
    });
};

export const formatChatMessagesFromServer = (
    messages: Array<IServerChatHistoryMessage>
): Array<IClientChatHistoryMessage> => {
    return messages?.map((message) => {
        return {
            id: message.id,
            message: message.message,
            createdAt: message.created_at,
            updatedAt: message.updated_at,
            toolChat: message.tool_chat,
            toolUser: message.tool_user,
            type: message.type,
            toolChatQuestionTemplate: message.tool_chat_question_template,
        };
    });
};

export const formatChatHistoryFromServer = (
    history: IServerChatHistory
): IClientChatHistory => {
    return {
        chatHistory:
            history.chat_history && history.chat_history.length > 0
                ? formatChatMessagesFromServer(history.chat_history)
                : [],
        generatedContent:
            history.generated_content && history.generated_content.length > 0
                ? formatChatMessagesFromServer(history.generated_content)
                : [],
    };
};

export const createDummyChatHistory = (
    activeChatQuestions: Array<ICompanyQuestion>,
    answerObject: IQuestionAnswer,
    myUserId: number
) => {
    const dummyHistory: IClientChatHistory = {
        chatHistory: [
            {
                id: TempMessageAndChatIds.TEMP_MESSAGE_ID,
                message: activeChatQuestions[0].title || '',
                toolChat: 0,
                toolUser: myUserId,
                type: MessageTypes.ASSISTANT,
                toolChatQuestionTemplate: activeChatQuestions[0],
            },
            {
                id: TempMessageAndChatIds.TEMP_MESSAGE_ID_1,
                message: answerObject.answer,
                toolChat: 0,
                toolUser: myUserId,
                type: MessageTypes.USER,
                toolChatQuestionTemplate: activeChatQuestions[0],
            },
            {
                id: TempMessageAndChatIds.TEMP_AWAITING_REPLY_MESSAGE_ID,
                message: 'Generating...',
                toolChat: 0,
                toolUser: myUserId,
                type: MessageTypes.ASSISTANT,
                toolChatQuestionTemplate: activeChatQuestions[0],
            },
        ],
        generatedContent: [],
    };
    return dummyHistory;
};

export const createDummyMessage = (
    message: string,
    myUserId: number,
    messageId: string,
    messageType: MessageTypes
) => {
    return {
        id: messageId,
        message: message,
        toolChat: 0,
        toolUser: myUserId,
        type: messageType,
        toolChatQuestionTemplate: null,
    };
};

const formatQuestionAnswerFromServer = (
    questionAnswer: ICompanyAnsweredQuestion
) => {
    const { options, type, answer } = questionAnswer;
    switch (type) {
        case CompanyQuestionType.SINGLE_SELECTION: {
            if (
                (typeof answer === 'string' || typeof answer === 'number') &&
                Array.isArray(options) &&
                options.length > 0 &&
                typeof options[0] === 'object'
            ) {
                const foundOption = (options as IOption[]).find(
                    (option) => Number(option.value) === Number(answer)
                );
                return foundOption ? foundOption.label : answer;
            }
            return answer;
        }
        case CompanyQuestionType.MULTI_SELECTION: {
            if (
                Array.isArray(answer) &&
                Array.isArray(options) &&
                options.length > 0 &&
                typeof options[0] === 'object'
            ) {
                const labels = answer
                    .map((ans) => {
                        const numAns = Number(ans);
                        const foundOption = (options as IOption[]).find(
                            (option) => Number(option.value) === numAns
                        );
                        return foundOption ? foundOption.label : null;
                    })
                    .filter((label): label is string => label !== null);
                return labels.join(', ');
            }
            return Array.isArray(answer) ? answer.join(', ') : answer;
        }
        case CompanyQuestionType.FILE_UPLOAD:
            return answer;
        case CompanyQuestionType.BOOLEAN:
            return answer;
        case CompanyQuestionType.LONG_TEXT:
            if (typeof answer === 'string') {
                return stripHtml(answer);
            }
            return answer;
        case CompanyQuestionType.SHORT_TEXT:
            return answer;
        default:
            return answer;
    }
};

const getAnswerOptions = (
    questionAnswer: ICompanyAnsweredQuestion
): IOption[] | undefined => {
    const { options, type, answer } = questionAnswer;

    if (!Array.isArray(options) || options.length === 0) {
        return undefined;
    }
    const safeOptions = options as IOption[];

    switch (type) {
        case CompanyQuestionType.SINGLE_SELECTION:
            if (typeof answer === 'string' || typeof answer === 'number') {
                const foundOption = safeOptions.find(
                    (option) => Number(option.value) === Number(answer)
                );
                return foundOption ? [foundOption] : undefined;
            }
            break;

        case CompanyQuestionType.MULTI_SELECTION:
            if (Array.isArray(answer)) {
                const selectedOptions = answer
                    .map((ans) => {
                        const numAns = Number(ans);
                        return safeOptions.find(
                            (option) => Number(option.value) === numAns
                        );
                    })
                    .filter((option): option is IOption => !!option);
                return selectedOptions.length > 0 ? selectedOptions : undefined;
            }
            break;
        default:
            return undefined;
    }
    return undefined;
};

export const formatCompanyQuestionsHistoryFromServer = (
    answeredQuestions: Array<ICompanyAnsweredQuestion>
) => {
    return answeredQuestions.map((question) => {
        return {
            id: question.id,
            title: question.title,
            subtitle: question.subtitle,
            options: question.options,
            type: question.type,
            category: question.category,
            index: question.index,
            answer: formatQuestionAnswerFromServer(question),
            answerOptions: getAnswerOptions(question),
            weight: question.weight,
        };
    });
};
